import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BreadcrumbService } from '../../../../domain/services/breadcrumb/breadcrumb.service';
import { FeesService } from '../../../../domain/services/fees/fees.service';
import { FeeModel, FeeHistoryModel, FeeTier } from '../../../../domain/models/fee.model';

@Component({
  selector: 'app-fee-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTableModule,
    MatTabsModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './fee-detail.component.html',
  styleUrl: './fee-detail.component.css',
})
export class FeeDetailComponent implements OnInit {
  fee: FeeModel | null = null;
  historyDataSource = new MatTableDataSource<FeeHistoryModel>([]);
  historyColumns = [
    'activeDateRange',
    'rate',
    'minimum',
    'maximum',
    'source',
    'changedBy',
    'changeDate',
  ];

  parametersForm!: FormGroup;
  showWarningBanner = false;
  warningEffectiveDate: string | null = null;
  originalEffectiveFrom: Date | null = null;
  isLoading = false;

  tiers: FeeTier[] = [];
  editingTierIndex: number | null = null;
  editingTier: FeeTier = { fee: '', from: '', to: '' };

  get isRangeStructure(): boolean {
    return this.fee?.structure === 'Range' || this.fee?.structure === 'Tiered';
  }

  addTier(): void {
    this.editingTierIndex = -1; // -1 means new
    this.editingTier = { fee: '', from: '', to: '' };
  }

  editTier(index: number): void {
    this.editingTierIndex = index;
    this.editingTier = { ...this.tiers[index] };
  }

  saveTier(): void {
    if (!this.editingTier.fee || !this.editingTier.from || !this.editingTier.to) return;
    if (this.editingTierIndex === -1) {
      this.tiers = [...this.tiers, { ...this.editingTier }];
    } else if (this.editingTierIndex !== null) {
      this.tiers = this.tiers.map((t, i) => i === this.editingTierIndex ? { ...this.editingTier } : t);
    }
    this.cancelEditTier();
  }

  cancelEditTier(): void {
    this.editingTierIndex = null;
    this.editingTier = { fee: '', from: '', to: '' };
  }

  removeTier(index: number): void {
    this.tiers = this.tiers.filter((_, i) => i !== index);
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly feesService: FeesService,
    private readonly fb: FormBuilder
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.params['id']);

    this.parametersForm = this.fb.group({
      rateValue: [null, Validators.required],
      minimum: [null],
      maximum: [null],
      source: ['All', Validators.required],
      description: [''],
      effectiveFrom: [null],
    });

    this.feesService.getFeeDetail(id).subscribe(({ fee, history }) => {
      this.fee = fee;
      this.historyDataSource.data = history;

      this.breadcrumbService.updateBreadcrumbs([
        { icon: 'account_balance', url: '/app/admin/fees', label: 'Administrative' },
        { icon: 'tune', url: '/app/admin/fees', label: 'Fee & Allowances' },
        { icon: 'tune', url: `/app/admin/fees/${fee.id}`, label: fee.name },
      ]);

      this.originalEffectiveFrom = fee.effectiveFrom ? new Date(fee.effectiveFrom) : null;

      if (fee.structure === 'Range' || fee.structure === 'Tiered') {
        this.tiers = fee.tiers ? [...fee.tiers] : [];
      }

      this.parametersForm.patchValue({
        rateValue: fee.rateValue ?? null,
        minimum: fee.minimum ?? null,
        maximum: fee.maximum ?? null,
        source: fee.source ?? 'All',
        description: fee.description ?? '',
        effectiveFrom: fee.effectiveFrom ? new Date(fee.effectiveFrom) : null,
      });

      this.parametersForm.get('effectiveFrom')?.valueChanges.subscribe((newDate: Date | null) => {
        if (newDate && this.originalEffectiveFrom) {
          const changed =
            newDate.getTime() !== this.originalEffectiveFrom.getTime();
          this.showWarningBanner = changed;
          if (changed) {
            this.warningEffectiveDate = newDate.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            });
          }
        } else if (newDate && !this.originalEffectiveFrom) {
          this.showWarningBanner = true;
          this.warningEffectiveDate = newDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          });
        } else {
          this.showWarningBanner = false;
          this.warningEffectiveDate = null;
        }
      });
    });
  }

  cancelDateChange(): void {
    this.parametersForm.patchValue({ effectiveFrom: this.originalEffectiveFrom });
    this.showWarningBanner = false;
    this.warningEffectiveDate = null;
  }

  onSave(): void {
    if (this.parametersForm.valid && this.fee) {
      this.isLoading = true;
      const formValue = this.parametersForm.value;
      const payload: any = {
        // Preserve identity fields
        name: this.fee.name,
        type: this.fee.type,
        structure: this.fee.structure,
        rateType: this.fee.rateType,
        // Editable fields
        rateValue: formValue.rateValue,
        minimum: formValue.minimum,
        maximum: formValue.maximum,
        source: formValue.source,
        description: formValue.description,
        effectiveFrom: formValue.effectiveFrom,
      };
      // Include tiers for Range/Tiered structure
      if (this.isRangeStructure) {
        payload.tiers = this.tiers;
      }
      this.feesService
        .updateFee(this.fee.id, payload)
        .subscribe(() => {
          this.isLoading = false;
          this.originalEffectiveFrom = formValue.effectiveFrom;
          this.showWarningBanner = false;
          this.warningEffectiveDate = null;
        }, (error: any) => {
          this.isLoading = false;
        });
    }
  }

  isFirstHistoryEntry(row: FeeHistoryModel): boolean {
    const data = this.historyDataSource.data;
    return data.length > 0 && data[0].id === row.id;
  }

  goBack(): void {
    this.router.navigate(['/app/admin/fees']);
  }
}
